import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"

// Only EN is loaded at init (fallback + initial). Other languages load on demand.
import en from "./locales/en"
import personalSettingsEn from "./locales/en/namespaces/personalSettings.js"
import businessSettingsEn from "./locales/en/namespaces/businessSettings.js"
import globalSettingsEn from "./locales/en/namespaces/globalSettings.js"
import einvoiceEn from "./locales/en/einvoice/index.js"
import messagesEn from "./locales/en/messages/index.js"
import settingsEn from "./locales/en/settings/index.js"
import productSettingsEn from "./locales/en/namespaces/productSettings.js"
import notificationCenterEn from "./locales/en/namespaces/notificationCenter.js"

export const SUPPORTED_LANGS = ["en", "es", "fr", "de", "it"]

/** Human-readable names for UI selects (native language names). Keep in sync with SUPPORTED_LANGS. */
const APP_LANGUAGE_LABELS = {
	en: "English",
	es: "Español",
	fr: "Français",
	de: "Deutsch",
	it: "Italiano",
}

export const APP_LANGUAGE_SELECT_OPTIONS = SUPPORTED_LANGS.map((code) => ({
	label: APP_LANGUAGE_LABELS[code],
	value: code,
}))

/**
 * Load a language bundle if not already loaded (lazy loading).
 * Resolves when the language is ready. Safe to call multiple times for the same lang.
 * @param {string} lng - Language code (en, es, fr, de, it)
 * @returns {Promise<void>}
 */
export async function loadLanguage(lng) {
  const normalized = (lng || "en").toLowerCase()
  if (!SUPPORTED_LANGS.includes(normalized)) {
    return
  }
  if (i18n.hasResourceBundle(normalized, "translation") && i18n.hasResourceBundle(normalized, "notificationCenter")) {
    return
  }
  if (normalized === "en") {
    // Already loaded at init
    return
  }

  const translationLoaded = i18n.hasResourceBundle(normalized, "translation")
  const [
    translation,
    personalSettings,
    businessSettings,
    globalSettings,
    einvoice,
    messages,
    settings,
    productSettings,
    notificationCenter,
  ] = await Promise.all([
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/index.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/namespaces/personalSettings.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/namespaces/businessSettings.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/namespaces/globalSettings.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/einvoice/index.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/messages/index.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/settings/index.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/namespaces/productSettings.js`),
    import(/* webpackChunkName: "locale-[request]" */ `./locales/${normalized}/namespaces/notificationCenter.js`),
  ])

  if (!translationLoaded) {
    i18n.addResourceBundle(normalized, "translation", translation.default)
    i18n.addResourceBundle(normalized, "personalSettings", personalSettings.default)
    i18n.addResourceBundle(normalized, "businessSettings", businessSettings.default)
    i18n.addResourceBundle(normalized, "globalSettings", globalSettings.default)
    i18n.addResourceBundle(normalized, "einvoice", einvoice.default)
    i18n.addResourceBundle(normalized, "messages", messages.default)
    i18n.addResourceBundle(normalized, "settings", settings.default)
    i18n.addResourceBundle(normalized, "productSettings", productSettings.default)
  }
  i18n.addResourceBundle(normalized, "notificationCenter", notificationCenter.default)
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    react: {
      useSuspense: false,
    },
    fallbackLng: "en",
    debug: false,
    supportedLngs: SUPPORTED_LANGS,
    interpolation: {
      escapeValue: false,
    },
    resources: {
      en: {
        translation: en,
        personalSettings: personalSettingsEn,
        businessSettings: businessSettingsEn,
        globalSettings: globalSettingsEn,
        einvoice: einvoiceEn,
        messages: messagesEn,
        settings: settingsEn,
        productSettings: productSettingsEn,
        notificationCenter: notificationCenterEn,
      },
    },
    saveMissing: false,
  })

export default i18n
